import { defineStore } from 'pinia'
import { createState, type PromoEngineState } from './state'

// Types
import {
  type SalesData,
  type PromoEngineApplyResponse,
  type PromoEnginePreviewResponseDataAttributes,
} from '@/api'

// Client API
import {
  previewPromotionsAuto,
  applyPromotionsAuto,
  estimatePromotionsManual,
  getCampaigns,
  applyPromotionsManual,
} from './../promo-engine-client'
import { salesApi } from '@/addons/axios'

// VUEX: import del root store
import rootVuex from '@/store'
import { ConfigGetters } from '@/store/configs-store'
import { SalesGetters, SalesActions } from '@/store/sales/sales-store'
import { NotificationsActions } from '@/store/notifications-store'

// i18n
import i18n from '@/addons/i18n'
import { LoyaltyGetters } from '@/modules/loyalty/store'
import { nowIsoLocalWithOffsetHHMM } from '@/addons/date'

export const usePromoEngineStore = defineStore('promo-engine', {
  state: (): PromoEngineState => ({
    ...createState(),
  }),

  /*
   * GETTERS Pinia
   */
  getters: {
    // IS_PROMO_ENGINE_ACTIVE -> isPromoEngineActive
    isPromoEngineActive(): boolean {
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      return (
        cfg?.PROMO_ENGINE_ENABLED === '2' &&
        cfg?.PROMO_ENGINE_USER_SELECTION === '0'
      )
    },

    isPromoEngineFidelityActive(): boolean {
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      return (
        cfg?.PROMO_ENGINE_ENABLED === '2' &&
        cfg?.PROMO_ENGINE_FIDELITY_ENABLED === '1'
      )
    },

    // IS_PROMO_ENGINE_PREVIEW_ENABLED -> isPromoEnginePreviewEnabled
    isPromoEnginePreviewEnabled(): boolean {
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      // la preview in manual sarà attiva quando promo enginw = 2 + manual attivo + preview attiva
      // TODO: verificare questo controlllo con i requirements: se l'anteprima è subordinata alla presenza attiva della modalità manuale
      return (
        cfg?.PROMO_ENGINE_ENABLED === '2' &&
        cfg?.PROMO_ENGINE_USER_SELECTION === '1' && // questo controllo può essere rismosso se l'anteprima non è correlata alla manuale
        cfg?.PROMO_ENGINE_PREVIEW_ENABLED === '1'
      )
    },

    // IS_PROMO_ENGINE_MANUAL_ACTIVE -> isPromoEngineManualActive
    isPromoEngineManualActive(): boolean {
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      return (
        cfg?.PROMO_ENGINE_ENABLED === '2' &&
        cfg?.PROMO_ENGINE_USER_SELECTION === '1'
      )
    },

    // GET_HAS_PROMO_ENGINE_ACTIVE -> getHasPromoEngineActive
    getHasPromoEngineActive(): boolean {
      const currentSale = rootVuex.getters?.[SalesGetters.GET_CURRENT_SALE]
      try {
        const dati_documenti = JSON.parse(currentSale?.dati_documenti ?? '{}')
        return (
          Object.keys(dati_documenti).length > 0 &&
          'promo_engine' in dati_documenti
        )
      } catch {
        return false
      }
    },

    // GET_PREVIEW_PROMO_ENGINE -> getPreviewPromoEngine
    getPreviewPromoEngine(): Array<PromoEnginePreviewResponseDataAttributes> {
      return this.previewPromoEngine
    },

    // Check Promo manuali attivabili
    hasActivableManualPromos: (state): boolean => {
      const list = state.manualPromoList ?? []
      return (
        Array.isArray(list) &&
        list.some((p) => Array.isArray(p?.effects) && p.effects.length > 0)
      )
    },
  },

  /*
   * ACTIONS Pinia
   */
  actions: {
    // RESET_STATE -> resetPromoEngineStore
    resetPromoEngineStore() {
      const fresh = createState()
      this.test = fresh.test
      this.previewPromoEngine = fresh.previewPromoEngine
      this.manualPromoList = fresh.manualPromoList
      this.consumerCampaigns = fresh.consumerCampaigns
      this.manualModalShown = fresh.manualModalShown
    },

    // APPLY_PROMOTIONS_AUTO -> applyPromotionsAuto
    async applyPromotionsAuto(payload: SalesData) {
      try {
        const resp: PromoEngineApplyResponse =
          await applyPromotionsAuto(payload)
        await rootVuex.dispatch(
          SalesActions.SET_SALE_DATI_PROMO,
          (resp as any)?.data?.attributes,
        )
        return resp
      } catch {
        // Notifiche già in clientapi
        return undefined
      }
    },

    // GET_CAMPAIGNS_PROMOTIONS -> getCampaignsPromotions
    async getCampaignsPromotions(): Promise<boolean> {
      try {
        const storeCode = rootVuex.state?.configs?.currentStore?.STORE_CODE
        const consumer = rootVuex.state.sales?.currentSale?.pk_consumer
        const resp = await getCampaigns(
          storeCode, // filterRetailCode
          'RUNNING', // filterLifecycle
          undefined, // filterActive
          1, // filterConditions
          undefined, // filterStartDate
          undefined, // filterEndDate,
          undefined, // filterPage,
          undefined, // filterSize,
          undefined, // filterLanguage,
          consumer, // filterConsumerCode,
          undefined, // filterIsJob,
          undefined, // params,
        )
        const results = resp?.results ?? []

        // commit in stato Pinia
        this.consumerCampaigns = results
        return results.length > 0
      } catch {
        // Notifiche già in clientapi
        return false
      }
    },

    // AUTO PROMO
    async _getSalePromotionsAuto(payload: SalesData): Promise<boolean> {
      try {
        const resp = await previewPromotionsAuto(payload)

        // selezione/normalizzazione AUTO: items + costs
        const attrs = (resp as any)?.data?.attributes ?? {}
        const items = Array.isArray(attrs?.items) ? attrs.items : []
        const costs = Array.isArray(attrs?.costs) ? attrs.costs : []

        // fail-fast: nessuna promo disponibile
        if (!items.length && !costs.length) {
          await rootVuex.dispatch(
            NotificationsActions.NOTIFY_WARN,
            i18n.global.t('pos_promo_engine.no_available_promotions_alert'),
          )
          return false
        }

        // check unico su "modified"
        const anyModified =
          items.some((i: any) => i?.modified) ||
          costs.some((c: any) => c?.modified)

        if (!anyModified) {
          await rootVuex.dispatch(
            NotificationsActions.NOTIFY_WARN,
            i18n.global.t('pos_promo_engine.no_available_promotions_alert'),
          )
          return false
        }

        // commit preview
        this.previewPromoEngine = attrs
        return true
      } catch {
        // Notifiche già gestite nel client API
        return false
      }
    },

    // MANUAL PROMO
    async _getSalePromotionsManual(payload): Promise<boolean> {
      try {
        const resp = await estimatePromotionsManual(payload)

        // output manuale
        const list = (resp as any)?.output ?? []

        if (!Array.isArray(list) || list.length === 0) {
          await rootVuex.dispatch(
            NotificationsActions.NOTIFY_WARN,
            i18n.global.t('pos_promo_engine.no_available_promotions_alert'),
          )
          return false
        }

        // commit lista manuale
        this.manualPromoList = list

        // salvo tutta la resp per la apply successiva
        this.currentEstimate = {
          input: resp?.input ?? {},
          output: resp?.output ?? [],
          rule_engine_version: resp?.rule_engine_version ?? '',
          maxed_out_promotions: resp?.maxed_out_promotions ?? {},
        }
        return true
      } catch {
        // Notifiche già gestite nel client API
        return false
      }
    },

    // HELPERS

    /**
     * Costruisce il payload per AUTO, assicurando:
     * - normalizzazione dei capi (codice_stato = 'CURRENT', rimozione campi effimeri)
     * - creazione della sale su backend se manca id_transazione
     * - id composto come `${cod_negozio},${id_transazione}`
     */
    async _buildAutoPayloadFromCurrentSale(): Promise<SalesData> {
      const getCurrentSale = rootVuex.getters?.[SalesGetters.GET_CURRENT_SALE]
      const saleData = getCurrentSale

      // normalizzazione capi
      if (Array.isArray(saleData?.capi)) {
        saleData.capi = saleData.capi.map((d: any) => {
          d.codice_stato = 'CURRENT'
          if ('layby_delete' in d) {
            delete d.layby_delete
          }
          if ('error_code' in d) {
            delete d.error_code
          }
          return d
        })
      }

      // crea sale se manca id_transazione
      const hasId =
        Object.prototype.hasOwnProperty.call(saleData, 'id_transazione') &&
        saleData.id_transazione !== undefined

      if (!hasId) {
        const sale = await salesApi.apiV1SalesPost({
          data: { type: 'sale', attributes: saleData },
          preserve_decimals: true,
        })
        await rootVuex.dispatch(
          SalesActions.STORE_CREATION_DATE,
          (sale as any)?.data?.data?.attributes?.data_creazione,
        )
        await rootVuex.dispatch(
          SalesActions.STORE_ID_TRANSAZIONE,
          (sale as any)?.data?.data?.attributes?.id_transazione,
        )
      }

      // ricava id aggiornato
      const saleId = getCurrentSale.id_transazione

      return {
        data: {
          type: 'sales',
          id: `${getCurrentSale?.cod_negozio},${saleId}`,
          attributes: getCurrentSale,
        },
        preserve_decimals: true,
      } as SalesData
    },

    // GET_SALE_PROMOTIONS -> getSalePromotions
    async getSalePromotions(): Promise<boolean> {
      try {
        if (this.isPromoEngineManualActive) {
          // blocchiamo apertura modale di scelta (unico punto)
          this.manualModalShown = true

          // flusso MANUAL
          const payload = await this._buildManualPayloadFromCurrentSale()
          return await this._getSalePromotionsManual(payload)
        }
        // flusso AUTO
        const payload = await this._buildAutoPayloadFromCurrentSale()
        return await this._getSalePromotionsAuto(payload)
      } catch {
        // Notifiche già gestite a livello di client API
        return false
      }
    },

    /**
     * Costruisce il payload "manual estimate":
     * - normalizzazione dei capi (codice_stato = 'CURRENT', rimozione campi effimeri)
     * - creazione della sale su backend se manca id_transazione
     * - session_code composto come `${cod_negozio}-${id_transazione}`
     */

    async _buildManualPayloadFromCurrentSale(): Promise<any> {
      // ---- DA STORE VUEX CORE ----|
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      // const storeInfo = rootVuex.state?.configs?.currentStore ?? {}
      const getCurrentSale = rootVuex.getters?.[SalesGetters.GET_CURRENT_SALE]
      const saleData = getCurrentSale
      const consumer = rootVuex.getters?.[SalesGetters.GET_CONSUMER]
      const loyalty = rootVuex.getters?.[LoyaltyGetters.GET_ENABLED_CARD]

      // ---- normalizzazione capi come AUTO ----
      if (Array.isArray(saleData?.capi)) {
        saleData.capi = saleData.capi.map((d: any) => {
          d.codice_stato = 'CURRENT'
          if ('layby_delete' in d) {
            delete d.layby_delete
          }
          if ('error_code' in d) {
            delete d.error_code
          }
          return d
        })
      }

      // ---- crea sale se manca id_transazione, come AUTO ----
      const hasId =
        Object.prototype.hasOwnProperty.call(saleData, 'id_transazione') &&
        saleData.id_transazione !== undefined

      if (!hasId) {
        const sale = await salesApi.apiV1SalesPost({
          data: { type: 'sale', attributes: saleData },
          preserve_decimals: true,
        })
        await rootVuex.dispatch(
          SalesActions.STORE_CREATION_DATE,
          (sale as any)?.data?.data?.attributes?.data_creazione,
        )
        await rootVuex.dispatch(
          SalesActions.STORE_ID_TRANSAZIONE,
          (sale as any)?.data?.data?.attributes?.id_transazione,
        )
      }
      // ricava id aggiornato
      const saleId = getCurrentSale.id_transazione

      // ---- shop info ---->
      const retail_code = cfg?.STORE_CODE
      const shop_country = cfg?.COUNTRY_CODE
      const shop_sign = cfg?.STORE_SIGN

      // ---- id / session ---->
      const idTransazione = String(saleData?.id_transazione ?? '')
      const code = idTransazione
      const session_code = `${getCurrentSale?.cod_negozio}-${saleId}`

      // ---- DATE ---->
      const creationDatetimeStr = saleData.data_creazione as string // "2025-10-01 15:39:47"
      // creation_date 'yyyyMMddHHmmss'
      const creation_date = creationDatetimeStr.replace(/[-: ]/g, '') // -> "20251001153947"

      // request_timestamp locale senza offset: 'yyyy-MM-ddTHH:mm:ss'
      // TODO: capire se e come inviare UTC +timeZone es.+2 request_timestamp: '2025-08-25T13:05:48+0200'
      const request_timestamp = nowIsoLocalWithOffsetHHMM()

      // ---- consumer (GET_CONSUMER) ---->
      const birth_day = String(consumer?.giorno_nascita ?? '')
      const birth_month = String(consumer?.mese_nascita ?? '')
      const birth_year = String(consumer?.anno_nascita ?? '')
      const email = String(consumer?.email ?? '')
      const tel_1 = String(consumer?.telefono1 ?? '')
      const residence_state = String(consumer?.nazione_iso ?? '')

      // ---- loyalty (GET_ENABLED_CARD) ---->
      const card_type = String(loyalty?.chiave ?? '')
      const card_code = String(loyalty?.valore ?? '')
      const card_level = String(loyalty?.fascia_attuale ?? '')
      const card_real_balance = loyalty?.current_value ?? undefined
      const card_state = String(loyalty?.status ?? '')
      const issue_date = loyalty?.data_inizio
        ? `${loyalty.data_inizio}000000`
        : ''
      const expiration_date = loyalty?.data_fine
        ? `${loyalty.data_fine}000000`
        : ''

      // ---- CAPI/items ---->
      const parseCustom = (s?: string) => {
        try {
          return s ? JSON.parse(s) : {}
        } catch {
          return {}
        }
      }

      const items = Array.isArray(saleData?.capi)
        ? saleData.capi.map((r: any) => {
            const cd = parseCustom(r?.custom_data)
            const annostag = String(cd?.annostag ?? '')
            const year = annostag.slice(0, 4) || ''
            const season = annostag.slice(4) || ''
            const retailBase = r?.importo_iniziale ?? 0
            const finalPrice = r?.importo_finale ?? retailBase
            return {
              shop_barcode: r?.sku, // ?? r?.sku_read ?? r?.ean ?? '',
              id: r?.progressivo,
              style_code: cd?.modello ?? '',
              style_code_variant: cd?.modello
                ? `${cd.modello}${cd.variante ?? ''}`
                : '',
              style_name: r?.nome ?? '',
              style_variant: cd?.variante ?? '',
              size: cd?.desc_taglia ?? '',
              year,
              season,
              class: r?.classe ?? '',
              exit: '', //  TODO: ? NON SO
              discount_on_item: r?.sconto ?? 0,
              collection: '', //  TODO: ? NON SO
              brand: '', //  TODO: ? NON SO
              seasonality: 'SEASONAL', //  TODO: ? NON SO
              product_category: r?.tipologia_merce ?? '',
              retail_price: retailBase,
              base_price: retailBase,
              pricelist_type: r?.tipo_importo ?? '', // ?? 'V',
              final_price: finalPrice,
              original_final_price: retailBase,
              tailor_expenses: 0,
              discount_fidelity: 0,
            }
          })
        : []

      // ---->
      //  payload input manual
      // ---->
      const fullPayload = {
        config: {
          language: cfg?.GUI_LANGUAGE_LOCALE,
          datetime_format: 'FLAT_DATETIME', // TODO:  ? NON SO
        },
        shop: { retail_code, shop_country, shop_sign },
        status: 'OPEN', // saleData?.codice_stato, // 'OPEN', // TODO: ? NON SO
        code,
        session_code,
        creation_date,
        request_timestamp,
        consumer: {
          consumer_code: saleData?.pk_consumer ?? '',
          name: consumer?.nome ?? undefined,
          surname: consumer?.cognome ?? undefined,
          birth_day,
          birth_month,
          birth_year,
          email,
          tel_1,
          residence_state,
          fidelity: {
            card_type,
            card_code,
            card_level: card_level || undefined,
            card_real_balance: card_real_balance || undefined,
            card_state: card_state || undefined,
            issue_date: issue_date || undefined,
            expiration_date: expiration_date || undefined,
          },
        },
        payments: Array.isArray(saleData?.pagamenti) ? saleData.pagamenti : [],
        type_cart: saleData?.codice_movimento, // SALE
        shipping_method: {},
        items,
        costs: [],
        discount_fidelity: 0, // TODO: ? NON SO
      }

      return fullPayload
    },

    /**
     * Apply manual (preview o checkout)
     * persist=false  → preview (checkout undefined)
     * persist=true   → checkout = '1' (salvataggio lato promo)
     */
    async applyManual(
      selectedPromos: { code: string; [k: string]: any }[],
      persist = false,
    ) {
      // 1) input: usa quello della currentEstimate se presente, altrimenti ricostruisce
      const input =
        this.currentEstimate?.input ??
        (await this._buildAutoPayloadFromCurrentSale())?.input

      // 2) destrutturo per cambio la chiave code --> promo
      const promotions = selectedPromos.map(({ code, ...sPromo }) => ({
        promo: code,
        ...sPromo,
      }))

      const payload = { input, promotions }

      // 3) chiamata client: preview (undefined) o checkout ('1')
      const checkout = persist ? '1' : undefined
      const resp = await applyPromotionsManual(payload, checkout)

      this.currentManualPreview = mapApplyResponseToPreview(resp)

      return this.currentManualPreview
    },
  },
})

// Mapper apply Payload --> { items, costs, promo }
function mapApplyResponseToPreview(resp: any) {
  const out = resp?.output ?? {}
  const itemsOut = Array.isArray(out.items) ? out.items : []
  const costsOut = Array.isArray(out.costs) ? out.costs : []
  const changes = Array.isArray(resp?.changes) ? resp.changes : []

  const items = itemsOut.map((it: any) => {
    const list_price = Number(it?.retail_price ?? 0)
    const price_before = Number(it?.original_final_price ?? 0)
    const price_after = Number(it?.final_price ?? 0)
    return {
      sku: String(it?.shop_barcode ?? ''),
      name: String(it?.style_name ?? ''),
      list_price,
      price_before,
      price_after,
      modified: price_before !== price_after,
      promo_engine: Array.isArray(it?.promo_engine) ? it.promo_engine : [],
      id: it?.id ?? null,
    }
  })

  const costs = costsOut.map((c: any) => {
    const price_before = Number(c?.original_final_price ?? 0)
    const price_after = Number(c?.final_price ?? 0)
    return {
      id: c?.id ?? null,
      type: String(c?.cost_type_trad ?? ''),
      list_price: price_before, // per i costi il listino coincide con original_final_price
      price_before,
      price_after,
      modified: price_before !== price_after,
      sku: String(c?.sku ?? ''),
      promo_engine: Array.isArray(c?.promo_engine) ? c.promo_engine : [],
    }
  })

  const promo = changes.map((ch: any) => ({
    code: ch?.promo ?? '',
    informations: ch?.informations ?? {},
    effects: Array.isArray(ch?.effects) ? ch.effects : [],
  }))

  return { items, costs, promo }
}

export type PromoEngineStore = ReturnType<typeof usePromoEngineStore>
